import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, EyeOff, KeyRound, MapPin, Hash, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Location {
  id: string;
  name: string;
}

interface Tavolo {
  id: string;
  location_id: string;
  table_number: number;
  pin: string;
  capacity: number;
  status: string;
}

function generatePin(): string {
  return String(Math.floor(Math.random() * 10000)).padStart(4, '0');
}

const Tavoli: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [tavoli, setTavoli] = useState<Tavolo[]>([]);
  const [tableNumber, setTableNumber] = useState('');
  const [capacity, setCapacity] = useState('4');
  const [loading, setLoading] = useState(false);
  const [showPins, setShowPins] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (selectedLocationId) {
      loadTavoli();
    } else {
      setTavoli([]);
    }
  }, [selectedLocationId]);

  const loadLocations = async () => {
    const { data, error } = await supabase
      .from('locations')
      .select('id, name')
      .eq('status', 'active')
      .order('name');

    if (error) {
      console.error('Error loading locations:', error);
      return;
    }
    setLocations(data || []);
    if (data && data.length > 0 && !selectedLocationId) {
      setSelectedLocationId(data[0].id);
    }
  };

  const loadTavoli = async () => {
    const { data, error } = await supabase
      .from('tavoli')
      .select('*')
      .eq('location_id', selectedLocationId)
      .order('table_number');

    if (error) {
      console.error('Error loading tavoli:', error);
      return;
    }
    setTavoli(data || []);
  };

  const handleAdd = async () => {
    if (!selectedLocationId || !tableNumber) return;

    const num = parseInt(tableNumber, 10);
    if (isNaN(num) || num < 1) {
      setError('Inserisci un numero di tavolo valido.');
      return;
    }

    const cap = parseInt(capacity, 10);
    if (isNaN(cap) || cap < 1) {
      setError('Inserisci una capacita valida.');
      return;
    }

    if (tavoli.some(t => t.table_number === num)) {
      setError(`Il tavolo ${num} esiste gia per questo locale.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase
        .from('tavoli')
        .insert({
          location_id: selectedLocationId,
          table_number: num,
          pin: generatePin(),
          capacity: cap,
          status: 'libero',
        });

      if (error) throw error;

      setTableNumber('');
      await loadTavoli();
    } catch (err: any) {
      console.error('Error adding tavolo:', err);
      if (err.message?.includes('duplicate')) {
        setError(`Il tavolo ${num} esiste gia per questo locale.`);
      } else {
        setError(err.message || 'Errore durante l\'aggiunta del tavolo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tavolo: Tavolo) => {
    if (!confirm(`Eliminare il Tavolo ${tavolo.table_number}?`)) return;

    try {
      const { error } = await supabase
        .from('tavoli')
        .delete()
        .eq('id', tavolo.id);

      if (error) throw error;
      await loadTavoli();
    } catch (err: any) {
      console.error('Error deleting tavolo:', err);
      setError(err.message || 'Errore durante l\'eliminazione.');
    }
  };

  const handleRegeneratePin = async (tavolo: Tavolo) => {
    try {
      const newPin = generatePin();
      const { error } = await supabase
        .from('tavoli')
        .update({ pin: newPin })
        .eq('id', tavolo.id);

      if (error) throw error;
      await loadTavoli();
    } catch (err: any) {
      console.error('Error regenerating PIN:', err);
      setError(err.message || 'Errore durante la rigenerazione del PIN.');
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'libero'
      ? 'text-emerald-600'
      : 'text-orange-600';
  };

  const getStatusLabel = (status: string) => {
    return status === 'libero' ? 'Libero' : 'Occupato';
  };

  const totalCapacity = tavoli.reduce((sum, t) => sum + t.capacity, 0);
  const freeTavoli = tavoli.filter(t => t.status === 'libero').length;
  const occupiedTavoli = tavoli.filter(t => t.status !== 'libero').length;

  return (
    <div className="p-8">
      {/* Stats banner */}
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Tavoli</p>
                  <p className="text-3xl font-bold text-white mt-1">{tavoli.length}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Hash className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Liberi</p>
                  <p className="text-3xl font-bold text-white mt-1">{freeTavoli}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <KeyRound className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Occupati</p>
                  <p className="text-3xl font-bold text-white mt-1">{occupiedTavoli}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Capacita Totale</p>
                  <p className="text-3xl font-bold text-white mt-1">{totalCapacity}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestione Tavoli</h1>
        <div className="flex items-center gap-3">
          <select
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            {locations.length === 0 && (
              <option value="">Nessun locale disponibile</option>
            )}
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowPins(!showPins)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              showPins
                ? 'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showPins ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPins ? 'Nascondi PIN' : 'Mostra PIN'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Add table form */}
      {selectedLocationId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              type="number"
              min="1"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Numero tavolo"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-rose-500 focus:border-transparent text-gray-700 placeholder-gray-400"
            />
            <input
              type="number"
              min="1"
              max="20"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="Capacita"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-rose-500 focus:border-transparent text-gray-700 placeholder-gray-400"
            />
            <button
              onClick={handleAdd}
              disabled={loading || !tableNumber}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-lg font-semibold hover:from-rose-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <Plus className="h-4 w-4" />
              Aggiungi
            </button>
          </div>
        </div>
      )}

      {/* Tables grid */}
      {tavoli.length === 0 && selectedLocationId ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
          <KeyRound className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-400">Nessun tavolo configurato</p>
          <p className="text-sm mt-1 text-gray-400">Aggiungi il primo tavolo per questo locale.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tavoli.map((tavolo) => (
            <div
              key={tavolo.id}
              className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 hover:shadow-md transition-all group relative"
            >
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900">
                  Tavolo {tavolo.table_number}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Capacita: {tavolo.capacity}
                </p>
                <p className={`text-sm font-semibold mt-1 ${getStatusColor(tavolo.status)}`}>
                  {getStatusLabel(tavolo.status)}
                </p>
              </div>

              <div className="mt-4 bg-gray-50 rounded-lg py-2.5 px-3 text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">PIN</p>
                <p className="text-xl font-mono font-bold text-gray-800 tracking-widest mt-0.5">
                  {showPins ? tavolo.pin : '****'}
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  onClick={() => handleRegeneratePin(tavolo)}
                  className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                  title="Rigenera PIN"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(tavolo)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Elimina tavolo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tavoli;
